import {fromJsonStrict, type JsonValue} from '@github-ui/dotcom-schema/protobuf'
import {
  AgentMergeEvaluationScopeSchema,
  type AgentMergeEvaluationScope,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {
  AgentMergeEvaluationScopeError,
  compareRevisions,
  isDecimalRevision,
  sameEvaluationConfigurationRecord,
  validEvaluationScope,
} from '../../helpers/agent-merge-status-validation'

export const AGENT_MERGE_ACTIONS = ['fix_ci', 'resolve_conflicts', 'respond_to_reviewers'] as const
export const AGENT_MERGE_UNAVAILABLE_MESSAGE = 'Agent Merge is temporarily unavailable.'

export type AgentMergeAction = (typeof AGENT_MERGE_ACTIONS)[number]

export type AgentMergeConfiguration = {
  enabled: boolean
  allowedActions: AgentMergeAction[]
}

export type AgentMergePolicy = {
  automationsPolicyDisabled: boolean
  automationsPolicyDisabledByOrganization: boolean
}

/**
 * Identifies the user whose authority an enabled Agent Merge assignment runs under. A disabled
 * assignment can retain a stale `ownerLogin` for audit history, so ownership fields are only
 * meaningful while `enabled` is true.
 */
export type AgentMergeOwnership = {
  ownerLogin: string | null
  viewerOwnsAssignment: boolean
}

export const AGENT_MERGE_CHECK_SCOPE_STATES = ['all_required', 'configured', 'invalid', 'unavailable'] as const

export type AgentMergeCheckScopeState = (typeof AGENT_MERGE_CHECK_SCOPE_STATES)[number]

export type AgentMergeCheckScope = {
  state: AgentMergeCheckScopeState
  configPath: string | null
  configUrl: string | null
  checkNames: string[]
  unmatchedCheckNames: string[] | null
  actionsOnly: boolean
}

export const AGENT_MERGE_ENROLLMENT_STATES = ['pending', 'succeeded', 'skipped', 'failed', 'unconfirmed'] as const

export type AgentMergeEnrollmentState = (typeof AGENT_MERGE_ENROLLMENT_STATES)[number]

/**
 * Tracks a background PR-creation enrollment attempt that has not yet produced (or will never
 * produce) an authoritative configuration. The backend omits this field once an authoritative
 * configuration -- including a disabled one -- is returned, so its mere presence means the
 * `enabled`/`allowedActions` values alongside it are not yet confirmed and must not be rendered
 * as-is.
 */
export type AgentMergeEnrollmentAttempt = {
  requestId: string
  state: AgentMergeEnrollmentState
  requestedAt: string
  deadlineAt: string
  reason: string | null
}

export type AgentMergeEvaluationConfigurationFence = {scope: AgentMergeEvaluationScope; cleared: boolean}

export type AgentMergeConfigurationResponse = AgentMergeConfiguration &
  Partial<AgentMergePolicy> &
  AgentMergeOwnership & {
    headSha: string
    etag: string | null
    configurationGeneration?: string
    displayGeneration?: string
    activityAvailable?: boolean
    evaluationStatusAvailable?: boolean
    evaluationScope?: AgentMergeEvaluationScope
    // Document-local read ordering, not a producer scope or server response field.
    evaluationFence?: AgentMergeEvaluationConfigurationFence
    fixCICheckScope: AgentMergeCheckScope | null
    automaticEnrollment?: AgentMergeEnrollmentAttempt
    viewerAutomaticEnrollmentEnabled?: boolean
  }

type AgentMergeCheckScopeInput = {
  state?: string
  configPath?: unknown
  configUrl?: unknown
  checkNames?: readonly unknown[]
  unmatchedCheckNames?: readonly unknown[] | null
  actionsOnly?: unknown
}

type AgentMergeEnrollmentAttemptInput = {
  requestId?: unknown
  state?: unknown
  requestedAt?: unknown
  deadlineAt?: unknown
  reason?: unknown
}

export type AgentMergeConfigurationResponseInput = Omit<
  AgentMergeConfigurationResponse,
  | 'allowedActions'
  | 'fixCICheckScope'
  | 'automaticEnrollment'
  | 'evaluationStatusAvailable'
  | 'evaluationScope'
  | 'evaluationFence'
  | 'viewerAutomaticEnrollmentEnabled'
  | keyof AgentMergePolicy
  | keyof AgentMergeOwnership
> &
  Partial<AgentMergePolicy> &
  Partial<AgentMergeOwnership> & {
    allowedActions: readonly string[]
    fixCICheckScope?: AgentMergeCheckScopeInput | null
    automaticEnrollment?: AgentMergeEnrollmentAttemptInput | null
    evaluationStatusAvailable?: boolean
    evaluationScope?: JsonValue
    viewerAutomaticEnrollmentEnabled?: unknown
  }

export type AgentMergeErrorResponse = Partial<AgentMergePolicy> & {
  error?: string
  reason?: string
  automaticEnrollment?: AgentMergeEnrollmentAttemptInput | null
}

export class AgentMergeEnrollmentLoadError extends Error {
  readonly automaticEnrollment: AgentMergeEnrollmentAttempt

  constructor(message: string, automaticEnrollment: AgentMergeEnrollmentAttempt) {
    super(message)
    this.name = 'AgentMergeEnrollmentLoadError'
    this.automaticEnrollment = automaticEnrollment
  }
}

export function unresolvedAgentMergeEnrollment(
  data: AgentMergeConfigurationResponse | undefined,
  error: Error | null,
): AgentMergeEnrollmentAttempt | undefined {
  if (data?.etag) return undefined
  if (error) return error instanceof AgentMergeEnrollmentLoadError ? error.automaticEnrollment : undefined
  return data?.automaticEnrollment
}

export const DEFAULT_AGENT_MERGE_ACTION: AgentMergeAction = 'fix_ci'
export const AUTOMATIONS_POLICY_DISABLED_REASON = 'automations_policy_disabled'
export const UNAVAILABLE_AGENT_MERGE_CHECK_SCOPE: AgentMergeCheckScope = {
  state: 'unavailable',
  configPath: null,
  configUrl: null,
  checkNames: [],
  unmatchedCheckNames: null,
  actionsOnly: true,
}

export class AgentMergePolicyDisabledError extends Error {
  readonly policy: Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
    Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>>

  constructor(
    message: string,
    policy: Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
      Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>>,
  ) {
    super(message)
    this.name = 'AgentMergePolicyDisabledError'
    this.policy = policy
  }
}

function isAgentMergeAction(action: string): action is AgentMergeAction {
  return AGENT_MERGE_ACTIONS.some(candidate => candidate === action)
}

function isAgentMergeCheckScopeState(state: string): state is AgentMergeCheckScopeState {
  return AGENT_MERGE_CHECK_SCOPE_STATES.some(candidate => candidate === state)
}

function isAgentMergeEnrollmentState(state: string): state is AgentMergeEnrollmentState {
  return AGENT_MERGE_ENROLLMENT_STATES.some(candidate => candidate === state)
}

/**
 * Fails closed: any malformed attempt normalizes to `undefined` (no attempt) rather than a
 * guessed state, so a backend bug cannot strand the UI in a bogus "setting up" placeholder.
 */
export function normalizeAgentMergeEnrollmentAttempt(
  attempt: AgentMergeEnrollmentAttemptInput | null | undefined,
): AgentMergeEnrollmentAttempt | undefined {
  if (
    !attempt ||
    typeof attempt.requestId !== 'string' ||
    typeof attempt.state !== 'string' ||
    !isAgentMergeEnrollmentState(attempt.state) ||
    typeof attempt.requestedAt !== 'string' ||
    typeof attempt.deadlineAt !== 'string'
  ) {
    return undefined
  }

  return {
    requestId: attempt.requestId,
    state: attempt.state,
    requestedAt: attempt.requestedAt,
    deadlineAt: attempt.deadlineAt,
    reason: typeof attempt.reason === 'string' ? attempt.reason : null,
  }
}

export function normalizeAgentMergeCheckScope(
  scope: AgentMergeCheckScopeInput | null | undefined,
): AgentMergeCheckScope | null {
  if (!scope?.state || !isAgentMergeCheckScopeState(scope.state)) return null

  const checkNames =
    scope.state === 'configured'
      ? [
          ...new Set(
            (scope.checkNames ?? [])
              .filter((name): name is string => typeof name === 'string')
              .map(name => name.trim())
              .filter(Boolean),
          ),
        ]
      : []
  const checkNameSet = new Set(checkNames)
  const unmatchedCheckNames =
    scope.state === 'configured' && Array.isArray(scope.unmatchedCheckNames)
      ? [
          ...new Set(
            scope.unmatchedCheckNames
              .filter((name): name is string => typeof name === 'string')
              .map(name => name.trim())
              .filter(name => checkNameSet.has(name)),
          ),
        ]
      : null

  return {
    state: scope.state,
    configPath: typeof scope.configPath === 'string' ? scope.configPath : null,
    configUrl: typeof scope.configUrl === 'string' ? scope.configUrl : null,
    checkNames,
    unmatchedCheckNames,
    actionsOnly: typeof scope.actionsOnly === 'boolean' ? scope.actionsOnly : true,
  }
}

export function normalizeAgentMergePolicy(
  response: Partial<AgentMergePolicy>,
): Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
  Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>> {
  return {
    automationsPolicyDisabled: response.automationsPolicyDisabled === true,
    ...(response.automationsPolicyDisabledByOrganization === undefined
      ? {}
      : {automationsPolicyDisabledByOrganization: response.automationsPolicyDisabledByOrganization === true}),
  }
}

export function normalizeAgentMergeConfiguration(
  response: AgentMergeConfigurationResponseInput,
): AgentMergeConfigurationResponse {
  const {evaluationScope: rawEvaluationScope, ...configuration} = response
  const evaluationScope =
    response.evaluationStatusAvailable && rawEvaluationScope
      ? fromJsonStrict(AgentMergeEvaluationScopeSchema, rawEvaluationScope, {ignoreUnknownFields: true})
      : undefined
  if (response.evaluationStatusAvailable && (!evaluationScope || !validEvaluationScope(evaluationScope))) {
    throw new Error('Agent Merge evaluation status is missing its current authorized scope')
  }
  if (
    response.displayGeneration !== undefined &&
    (!isDecimalRevision(response.displayGeneration) ||
      (evaluationScope && response.displayGeneration !== evaluationScope.displayGeneration))
  ) {
    throw new AgentMergeEvaluationScopeError('Agent Merge configuration has conflicting display generations.')
  }
  const allowedActions = [...new Set(response.allowedActions.filter(isAgentMergeAction))]
  const policy =
    response.automationsPolicyDisabled === undefined && response.automationsPolicyDisabledByOrganization === undefined
      ? {}
      : normalizeAgentMergePolicy(response)

  return {
    ...configuration,
    ...policy,
    ...(evaluationScope ? {evaluationScope} : {}),
    evaluationFence: undefined,
    allowedActions: allowedActions.length > 0 ? allowedActions : [DEFAULT_AGENT_MERGE_ACTION],
    ownerLogin: response.ownerLogin ?? null,
    viewerOwnsAssignment: response.viewerOwnsAssignment ?? false,
    fixCICheckScope: normalizeAgentMergeCheckScope(response.fixCICheckScope),
    automaticEnrollment: normalizeAgentMergeEnrollmentAttempt(response.automaticEnrollment),
    viewerAutomaticEnrollmentEnabled:
      response.viewerAutomaticEnrollmentEnabled === undefined ||
      typeof response.viewerAutomaticEnrollmentEnabled === 'boolean'
        ? (response.viewerAutomaticEnrollmentEnabled ?? false)
        : undefined,
  }
}

export class AgentMergeStaleConfigurationError extends Error {
  constructor() {
    super('Agent Merge configuration is older than the last confirmed state.')
    this.name = 'AgentMergeStaleConfigurationError'
  }
}

export function evaluationConfigurationFence(
  configuration: AgentMergeConfigurationResponse | undefined,
): AgentMergeEvaluationConfigurationFence | undefined {
  if (!configuration?.evaluationStatusAvailable) return undefined
  return (
    configuration.evaluationFence ??
    (configuration.evaluationScope?.configurationGeneration
      ? {scope: configuration.evaluationScope, cleared: false}
      : undefined)
  )
}

export function mergeEvaluationConfigurationFences(
  previous: AgentMergeEvaluationConfigurationFence | undefined,
  incoming: AgentMergeEvaluationConfigurationFence,
): AgentMergeEvaluationConfigurationFence {
  if (!previous || !sameEvaluationConfigurationRecord(previous.scope, incoming.scope)) return incoming
  const order = compareRevisions(incoming.scope.configurationGeneration, previous.scope.configurationGeneration)
  if (
    order === 0 &&
    incoming.scope.ownerUserId &&
    previous.scope.ownerUserId &&
    incoming.scope.ownerUserId !== previous.scope.ownerUserId
  ) {
    throw new AgentMergeEvaluationScopeError('Agent Merge configuration has conflicting assignment owners.')
  }
  if (
    order === 0 &&
    incoming.scope.displayGeneration &&
    previous.scope.displayGeneration &&
    incoming.scope.displayGeneration !== previous.scope.displayGeneration
  ) {
    throw new AgentMergeEvaluationScopeError('Agent Merge configuration has conflicting display cycles.')
  }
  if (
    order > 0 &&
    incoming.scope.displayGeneration &&
    previous.scope.displayGeneration &&
    compareRevisions(incoming.scope.displayGeneration, previous.scope.displayGeneration) < 0
  ) {
    return previous
  }
  if (order < 0) return previous
  if (order > 0) return incoming
  return {
    scope:
      incoming.scope.ownerUserId && (!previous.scope.displayGeneration || incoming.scope.displayGeneration)
        ? incoming.scope
        : previous.scope,
    cleared: previous.cleared || incoming.cleared,
  }
}

export function reconcileEvaluationConfiguration(
  current: AgentMergeConfigurationResponse | undefined,
  incoming: AgentMergeConfigurationResponse,
  {
    previousFence,
    confirmedWrite = false,
  }: {previousFence?: AgentMergeEvaluationConfigurationFence; confirmedWrite?: boolean} = {},
): AgentMergeConfigurationResponse {
  const scope = incoming.evaluationScope
  if (!incoming.evaluationStatusAvailable || !scope) return incoming
  const currentFence = evaluationConfigurationFence(current)
  const local = currentFence && sameEvaluationConfigurationRecord(currentFence.scope, scope) ? currentFence : undefined
  const remembered =
    previousFence && sameEvaluationConfigurationRecord(previousFence.scope, scope) ? previousFence : undefined
  const previous = local ? mergeEvaluationConfigurationFences(remembered, local) : remembered
  const candidate = scope.configurationGeneration
    ? {scope, cleared: confirmedWrite && !incoming.enabled}
    : previous && {scope: previous.scope, cleared: confirmedWrite && !incoming.enabled}
  const fence = candidate ? mergeEvaluationConfigurationFences(previous, candidate) : undefined
  const order =
    previous && scope.configurationGeneration
      ? compareRevisions(scope.configurationGeneration, previous.scope.configurationGeneration)
      : undefined
  const staleDisplay =
    !!previous?.scope.displayGeneration &&
    !!scope.configurationGeneration &&
    (!scope.displayGeneration || compareRevisions(scope.displayGeneration, previous.scope.displayGeneration) < 0)
  if (order === -1 || staleDisplay || (order === 0 && previous?.cleared && incoming.enabled)) {
    if (
      current &&
      local &&
      previous &&
      compareRevisions(local.scope.configurationGeneration, previous.scope.configurationGeneration) === 0 &&
      local.scope.displayGeneration === previous.scope.displayGeneration &&
      local.cleared === previous.cleared
    ) {
      return current
    }
    throw new AgentMergeStaleConfigurationError()
  }
  return {
    ...incoming,
    evaluationFence: fence,
  }
}
